import { ref, reactive } from "vue-demi";
import { defineStore } from "pinia";
import { formData } from "@utils";
import { useToast } from "vue-toastification/composition";

export const useSupplierApprovalStore = defineStore("supplierApproval", () => {
  const toast = useToast();

  const imageDialog = ref(false);
  const imageFile = ref([]);
  const imageUploadLoading = ref(false);
  const imageUploadKey = ref(null);

  const uploadedFilesByDocKey = reactive({
    isletmeKayit: [],
    urunSpes: [],
    kaliteBelgesi: [],
    guncelAnaliz: [],
    taklitTagsis: [],
    radyoaktivite: [],
    gdo: [],
    helal: [],
  });

  // RFC4122 UUID v4 üretimi. Destek varsa crypto.randomUUID kullanır
  const generateUuidV4 = () => {
    try {
      if (typeof window !== "undefined" && window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    // Fallback (v4 benzeri)
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const openImageDialog = (docKey) => {
    imageDialog.value = true;
    imageUploadKey.value = docKey;
    imageFile.value = [];
  };

  const getUploadedCount = (docKey) => (uploadedFilesByDocKey[docKey] || []).length;

  const removeUploaded = async (docKey, index) => {
    const list = uploadedFilesByDocKey[docKey] || [];
    const item = list[index];
    if (!item) return;
    // Local remove first (optimistic)
    list.splice(index, 1);
    uploadedFilesByDocKey[docKey] = [...list];
    // If backend supports removal, call it
    try {
      if (item && item.id) {
        const data = new FormData();
        data.append('method', 'vendorFormOperations');
        data.append('func', 'deleteFile');
        data.append('uploadid', typeof item === 'string' ? item : item.id);
        await formData(data);
      }
    } catch (e) {
      // rollback on failure
      uploadedFilesByDocKey[docKey].splice(index, 0, item);
    }
  };

  const imageSaveFn = async () => {
    if (!imageUploadKey.value) {
      toast.error("Belge anahtarı bulunamadı.");
      return;
    }
    if (!imageFile.value || imageFile.value.length === 0) {
      toast.error("Lütfen yüklenecek dosya(ları) seçiniz.");
      return;
    }
    imageUploadLoading.value = true;
    try {
      const baseKey = imageUploadKey.value;

      for (let i = 0; i < imageFile.value.length; i++) {
        const uploadId = generateUuidV4();

        const data = new FormData();
        data.append("method", "vendorFormOperations");
        data.append("func", "addMediaVendor");
        data.append("uploadid", uploadId);
        data.append("file", imageFile.value[i]);
        // media_type: requiredDocs key (örn: isletmeKayit, urunSpes, ...)
        data.append("media_type", baseKey);

        const response = await formData(data);
        if (response && Number(response.error) === 0) {
          // Sunucu dönen uploadid'i tercih et, yoksa yerelde üretileni kullan
          const storedId = response.uploadid || uploadId;
          uploadedFilesByDocKey[baseKey].push({
            id: storedId,
            name: imageFile.value[i]?.name || "dosya",
            size: imageFile.value[i]?.size || 0,
          });
          // Re-render tetikle: yeni referans ata
          uploadedFilesByDocKey[baseKey] = [...uploadedFilesByDocKey[baseKey]];
        } else {
          const msg = (response && response.msg) || "Yükleme başarısız oldu";
          toast.error(`${baseKey}: ${msg}`);
          break;
        }
      }
      imageDialog.value = false;
    } catch (e) {
      toast.error(`Hata ${e}`);
    } finally {
      imageUploadLoading.value = false;
      imageFile.value = [];
    }
  };

  const validateRequiredDocs = (requiredDocs) => {
    const missing = requiredDocs
      .filter((d) => d.key !== "helal")
      .filter((d) => getUploadedCount(d.key) === 0)
      .map((d) => d.title);
    return { ok: missing.length === 0, missing };
  };

  // Form kaydı sırasında backend'e gönderilecek uploadid listeleri
  const getUploadIdsPayload = () => {
    const payload = {};
    Object.keys(uploadedFilesByDocKey).forEach((k) => {
      payload[k] = [...uploadedFilesByDocKey[k]].map((f) => (typeof f === 'string' ? f : f.id));
    });
    return payload;
  };

  return {
    imageDialog,
    imageFile,
    imageUploadLoading,
    imageUploadKey,
    uploadedFilesByDocKey,
    openImageDialog,
    imageSaveFn,
    getUploadedCount,
    validateRequiredDocs,
    getUploadIdsPayload,
    removeUploaded,
  };
});


